import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./TeamLeadDashboard.css";
import "./MonthlyReport.css";
import MonthlyReport from "./UserMonthlyReport";
import ProfilePage from "./ProfilePage";
import {
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiLock,
  FiAward,
  FiTarget,
  FiUsers,
  FiSettings,
  FiTrendingUp,
  FiBell,
  FiFileText,
  FiPhoneCall,
  FiCheckSquare,
} from "react-icons/fi";
import {
  Box,
  Alert,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";
import api from "../api/axiosConfig";
import SalesTracker from "./SalesTracker";
import UserManagement from "./UserManagement";
import defaultAvatar from "../images/download.png";
import logo1 from "../images/logo.jpg";

const toTitleCase = (str) => {
  if (!str) return "";
  const withSpaces = str.replace(/_/g, " ").replace(/([A-Z])/g, " $1");
  return withSpaces.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isUserOnLeaveOnDate = (user, dateStr) => {
  if (!user || !user.leaveStatus || !user.leaveStartDate) return false;
  const checkDate = new Date(dateStr + "T00:00:00");
  const startDate = new Date(user.leaveStartDate);
  const endDate = user.leaveEndDate ? new Date(user.leaveEndDate) : null;

  checkDate.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(0, 0, 0, 0);

  if (endDate) {
    return checkDate >= startDate && checkDate <= endDate;
  }
  return checkDate >= startDate;
};

// Function to check if a date is a government holiday in India
const isIndianGovernmentHoliday = (date, corporateHolidays = []) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Check against corporate holidays from API
  const holidayFromAPI = corporateHolidays.find((holiday) => {
    const holidayDate = new Date(holiday.date);
    return (
      holidayDate.getFullYear() === year &&
      holidayDate.getMonth() === month &&
      holidayDate.getDate() === day
    );
  });

  if (holidayFromAPI) {
    return {
      name: holidayFromAPI.name,
      type: holidayFromAPI.type,
    };
  }

  // Fallback to basic fixed holidays if API fails (for redundancy)
  const fixedHolidays = [
    { month: 0, day: 1, name: "New Year's Day" },
    { month: 0, day: 26, name: "Republic Day" },
    { month: 7, day: 15, name: "Independence Day" },
    { month: 9, day: 2, name: "Gandhi Jayanti" },
    { month: 11, day: 25, name: "Christmas" },
    { month: 4, day: 1, name: "Labour Day" },
  ];

  const fixedHoliday = fixedHolidays.find(
    (holiday) => holiday.month === month && holiday.day === day
  );
  if (fixedHoliday) {
    return {
      name: fixedHoliday.name,
      type: "national",
    };
  }

  return null;
};

const TeamLeadDashboard = () => {
  const navigate = useNavigate();
  const { section, "*": restOfUrl } = useParams();
  const currentSection = section || "dashboard";

  let targetUserId = "me";
  let entryDateForTracker = formatDateForInput(new Date());

  if (section === "input-for" && restOfUrl) {
    const parts = restOfUrl.split("/");
    targetUserId = parts[0];
    if (parts[1]) entryDateForTracker = parts[1];
  } else if (section === "input" && restOfUrl) {
    entryDateForTracker = restOfUrl;
  }

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName] = useState(localStorage.getItem("name") || "User");
  const userRole = localStorage.getItem("userRole");
  const [user, setUser] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  // <<< STEP 1: ADD NEW STATE FOR STATIC KPI DATA >>>
  const [kpiData, setKpiData] = useState({ kpis: {}, previousMonthKpis: {} });
  const [teamSubmissionHistory, setTeamSubmissionHistory] = useState([]);
  const [rms, setRms] = useState([]);

  const today = new Date();
  const firstDayOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );
  const lastDayOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  );

  const [myPerformanceStartDate, setMyPerformanceStartDate] = useState(
    firstDayOfCurrentMonth
  );
  const [myPerformanceEndDate, setMyPerformanceEndDate] = useState(
    lastDayOfCurrentMonth
  );

  const [teamDataStartDate, setTeamDataStartDate] = useState(
    firstDayOfCurrentMonth
  );
  const [teamDataEndDate, setTeamDataEndDate] = useState(lastDayOfCurrentMonth);

  const [teamCalendarStartDate, setTeamCalendarStartDate] = useState(
    firstDayOfCurrentMonth
  );
  const [teamCalendarEndDate, setTeamCalendarEndDate] = useState(
    lastDayOfCurrentMonth
  );

  const [dashboardSearchTerm, setDashboardSearchTerm] = useState("");
  const [showTeamSubmissionsModal, setShowTeamSubmissionsModal] =
    useState(false);
  const [selectedTeamDate, setSelectedTeamDate] = useState(null);
  const [missingRMs, setMissingRMs] = useState([]);
  const [selectedField1, setSelectedField1] = useState("dailyPoints");
  const [selectedField2, setSelectedField2] = useState("none");
  const [selectedTeamField1, setSelectedTeamField1] = useState("totalPoints");
  const [selectedLeaderboardMetric, setSelectedLeaderboardMetric] =
    useState("aggregatedPoints");

  const [notifications, setNotifications] = useState([]);
  const [leaveRejectDialogOpen, setLeaveRejectDialogOpen] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [leaveRejectionReason, setLeaveRejectionReason] = useState("");
  const [leaveRevocationReason, setLeaveRevocationReason] = useState("");
  const [selectedNotificationTab, setSelectedNotificationTab] =
    useState("pending-approvals");
  const [pendingRMLeaveRequests, setPendingRMLeaveRequests] = useState([]);
  const [approvedRMLeaveRequests, setApprovedRMLeaveRequests] = useState([]);
  const [leaveRevokeDialogOpen, setLeaveRevokeDialogOpen] = useState(false);
  const [selectedLeaveRevokeRequestId, setSelectedLeaveRevokeRequestId] =
    useState(null);
  const [ownLeaveRequests, setOwnLeaveRequests] = useState([]);
  const [leaveRequestDialogOpen, setLeaveRequestDialogOpen] = useState(false);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    reason: "",
    start_date: "",
    end_date: "",
  });
  const [isOnLeave, setIsOnLeave] = useState(false);
  const [leaveDurationDays, setLeaveDurationDays] = useState(0);
  const [leaveEndDate, setLeaveEndDate] = useState(null);
  const [rmSubmissions, setRmSubmissions] = useState([]);
  const [transformedTeamData, setTransformedTeamData] = useState([]);
  const [calendarDataLoaded, setCalendarDataLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [corporateHolidays, setCorporateHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  // Hierarchical chart state
  const [chartLevel, setChartLevel] = useState("zonal"); // 'zonal' | 'branch_manager' | 'team_lead' | 'relationship_manager'
  const [chartSelectedUserId, setChartSelectedUserId] = useState(null);
  const [chartSelectedUserName, setChartSelectedUserName] = useState("");
  const [chartNavigationHistory, setChartNavigationHistory] = useState([]);
  const [chartCurrentLevelUsers, setChartCurrentLevelUsers] = useState([]);
  const [showChartCurrentLevelUsersModal, setShowChartCurrentLevelUsersModal] =
    useState(false);
  const [chartViewMode, setChartViewMode] = useState("chart"); // 'chart' | 'calendar'

  const chartFieldOptions = [
    { value: "dailyPoints", label: "Daily Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
    { value: "none", label: "None" },
  ];

  const leaderboardFieldOptions = [
    { value: "aggregatedPoints", label: "Aggregated Points" },
    ...chartFieldOptions.filter(
      (option) => !["dailyPoints", "none"].includes(option.value)
    ),
  ];

  const teamChartFieldOptions = [
    { value: "totalPoints", label: "Total Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
  ];

  const getMetric = (rm = {}, key) => {
    if (!rm || !key) return 0;
    const dataKey = key === "dailyPoints" ? "aggregatedPoints" : key;
    const value = rm[dataKey];
    if (value !== undefined && value !== null) {
      const parsed = Number(value);
      return !isNaN(parsed) ? parsed : 0;
    }
    return 0;
  };

  const transformTeamPerformanceData = useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      name: item.name || item.username || "Unknown",
      totalPoints: getMetric(item, "aggregatedPoints"),
      telephoneSalesCalls: getMetric(item, "telephoneSalesCalls"),
      faceToFace: getMetric(item, "faceToFace"),
      appointmentMade: getMetric(item, "appointmentMade"),
      appointmentKept: getMetric(item, "appointmentKept"),
      factFind: getMetric(item, "factFind"),
      serviceReviews: getMetric(item, "serviceReviews"),
      socialCalls: getMetric(item, "socialCalls"),
      highIncomeWealthy: getMetric(item, "highIncomeWealthy"),
      referrals: getMetric(item, "referrals"),
      closingInterview: getMetric(item, "closingInterview"),
      salesClose: getMetric(item, "salesClose"),
    }));
  }, []);

  const fetchProfileForHeader = useCallback(async () => {
    try {
      const { data: userData } = await api.get(
        `/api/users/me?t=${new Date().getTime()}`
      );
      if (userData) {
        const photoPath = userData.profilePhoto || userData.profile_photo;
        let fullUrl = defaultAvatar;
        if (photoPath) {
          if (
            photoPath.startsWith("http://") ||
            photoPath.startsWith("https://")
          ) {
            fullUrl = photoPath;
          } else {
            const serverOrigin = new URL(api.defaults.baseURL).origin;
            const cleanPath = photoPath.replace(/^\/+/, "");
            fullUrl = `${serverOrigin}/${cleanPath}`;
          }
        }
        setUser({ ...userData, profile_photo: fullUrl });
        localStorage.setItem("name", userData.name || userData.username);
        localStorage.setItem("userId", userData.user_id);
      }
    } catch (error) {
      console.error("Failed to fetch user profile for header:", error);
      setUser((prev) => ({ ...prev, profile_photo: defaultAvatar }));
    }
  }, []);

  const fetchLeaveStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/api/users/me/leave-status");
      setIsOnLeave(data.leaveStatus);
      if (data.leaveStatus && data.leaveStartDate) {
        setLeaveDurationDays(
          Math.max(
            1,
            Math.floor(
              (new Date() - new Date(data.leaveStartDate)) / 86400000
            ) + 1
          )
        );
        setLeaveEndDate(data.leaveEndDate || null);
      }
    } catch (error) {}
  }, []);

  // <<< STEP 2: ADD NEW USEEFFECT TO FETCH STATIC KPI DATA ONCE >>>
  useEffect(() => {
    const fetchInitialKpis = async () => {
      try {
        // These dates are fixed to the current month for the KPI cards
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        const { data } = await api.get(
          `/api/users/me/dashboard?startDate=${formatDateForInput(
            firstDay
          )}&endDate=${formatDateForInput(lastDay)}`
        );
        if (data) {
          setKpiData({
            kpis: data.kpis,
            previousMonthKpis: data.previousMonthKpis,
          });
        }
      } catch (err) {
        console.error("Failed to fetch initial KPI data:", err);
      }
    };

    if (currentSection === "dashboard") {
      fetchInitialKpis();
    }
  }, [currentSection]); // This runs only when the component mounts or the section switches to 'dashboard'

  // <<< STEP 3: MODIFY `fetchSectionData` TO HANDLE DYNAMIC DATA SEPARATELY >>>
  const fetchSectionData = useCallback(async () => {
    if (currentSection !== "dashboard" && currentSection !== "notifications") {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      if (currentSection === "dashboard") {
        const clientToday = formatDateForInput(new Date());
        await Promise.all([
          // This call now primarily provides dynamic data for the chart and calendar.
          // The KPI cards will NOT use the result of this call anymore.
          api
            .get(
              `/api/users/me/dashboard?startDate=${formatDateForInput(
                myPerformanceStartDate
              )}&endDate=${formatDateForInput(
                myPerformanceEndDate
              )}&clientToday=${clientToday}`
            )
            .then((res) => setDashboardData(res.data)),
          api
            .get(
              `/api/users/rms-by-team-lead/me?startDate=${formatDateForInput(
                teamDataStartDate
              )}&endDate=${formatDateForInput(teamDataEndDate)}`
            )
            .then((res) => setRms(res.data || [])),
          api
            .get(
              `/api/users/team-detailed-submission-history?startDate=${formatDateForInput(
                teamCalendarStartDate
              )}&endDate=${formatDateForInput(teamCalendarEndDate)}`
            )
            .then((res) => setTeamSubmissionHistory(res.data || [])),
        ]);
        // Set calendar data as loaded after all data is fetched
        setCalendarDataLoaded(true);
      } else if (currentSection === "notifications") {
        // ... this part remains the same
        const [own, pending, approved, notifs, subs] = await Promise.allSettled(
          [
            api.get("/api/users/me/my-leave-requests"),
            api.get("/api/users/leave-requests"),
            api.get("/api/users/team-leave-requests/approved"),
            api.get("/api/users/me/notifications"),
            api.get("/api/users/me/rm-submissions"),
          ]
        );
        if (own.status === "fulfilled")
          setOwnLeaveRequests(own.value.data || []);
        if (pending.status === "fulfilled")
          setPendingRMLeaveRequests(pending.value.data || []);
        if (approved.status === "fulfilled")
          setApprovedRMLeaveRequests(approved.value.data || []);
        if (notifs.status === "fulfilled")
          setNotifications(notifs.value.data || []);
        if (subs.status === "fulfilled")
          setRmSubmissions(subs.value.data || []);
      }
    } catch (err) {
      console.error(`Error fetching data for ${currentSection}:`, err);
      setError(`Failed to load data. Please refresh the page.`);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentSection,
    myPerformanceStartDate,
    myPerformanceEndDate,
    teamDataStartDate,
    teamDataEndDate,
    teamCalendarStartDate,
    teamCalendarEndDate,
  ]);

  useEffect(() => {
    fetchProfileForHeader();
    fetchLeaveStatus();
  }, [fetchProfileForHeader, fetchLeaveStatus]);

  useEffect(() => {
    fetchSectionData();
  }, [fetchSectionData]);

  useEffect(() => {
    setTransformedTeamData(
      rms?.length > 0 ? transformTeamPerformanceData(rms) : []
    );
  }, [rms, transformTeamPerformanceData]);

  // Corporate holidays - using fallback holidays only (no API fetch)
  useEffect(() => {
    setCorporateHolidays([]);
  }, []);

  // ... (rest of functions up to the return statement are the same)

  const handleTeamDateClick = (dateStr) => {
    const submittedEntries = teamSubmissionHistory.filter(
      (entry) => entry.date === dateStr
    );
    const submittedUserIds = new Set(
      submittedEntries.map((entry) => parseInt(entry.userId, 10))
    );
    const missing = rms.filter(
      (rm) => !rm.leaveStatus && !submittedUserIds.has(parseInt(rm.userId, 10))
    );
    setMissingRMs(missing);
    setSelectedTeamDate(dateStr);
    setShowTeamSubmissionsModal(true);
  };

  const handleApproveLeaveRequest = async (requestId) => {
    try {
      await api.put(`/api/users/leave-request/${requestId}/approve`);
      setSnackbar({
        open: true,
        message: "Leave request approved!",
        severity: "success",
      });
      fetchSectionData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error approving leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRejectDialog = (requestId) => {
    setSelectedLeaveRequestId(requestId);
    setLeaveRejectDialogOpen(true);
  };

  const handleRejectLeaveRequest = async () => {
    if (!leaveRejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for rejection.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRequestId}/reject`,
        { rejectionReason: leaveRejectionReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request rejected.",
        severity: "success",
      });
      setLeaveRejectDialogOpen(false);
      setLeaveRejectionReason("");
      fetchSectionData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error rejecting leave request.",
        severity: "error",
      });
    }
  };

  const handleSubmitLeaveRequest = async () => {
    if (!leaveRequestForm.reason.trim() || !leaveRequestForm.start_date) {
      setSnackbar({
        open: true,
        message: "Reason and start date are required.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.post("/api/users/me/leave-request", leaveRequestForm);
      setSnackbar({
        open: true,
        message: "Leave request submitted!",
        severity: "success",
      });
      setLeaveRequestDialogOpen(false);
      setLeaveRequestForm({ reason: "", start_date: "", end_date: "" });
      fetchSectionData();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Error submitting leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRevokeDialog = (requestId) => {
    setSelectedLeaveRevokeRequestId(requestId);
    setLeaveRevokeDialogOpen(true);
  };

  const handleRevokeLeaveRequest = async () => {
    if (!leaveRevocationReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for revocation.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRevokeRequestId}/revoke`,
        { revocationReason: leaveRevocationReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request revoked.",
        severity: "success",
      });
      setLeaveRevokeDialogOpen(false);
      setLeaveRevocationReason("");
      fetchSectionData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error revoking leave request.",
        severity: "error",
      });
    }
  };

  // Hierarchical chart navigation functions
  const navigateChartToLevel = async (level, userId, userName) => {
    setChartLevel(level);
    setChartSelectedUserId(userId);
    setChartSelectedUserName(userName);
    setChartNavigationHistory((prev) => [
      ...prev,
      {
        level: chartLevel,
        userId: chartSelectedUserId,
        userName: chartSelectedUserName,
      },
    ]);

    // Fetch users for the new level
    await fetchChartCurrentLevelUsers(level, userId);
  };

  const navigateChartBack = () => {
    if (chartNavigationHistory.length > 0) {
      const previousState =
        chartNavigationHistory[chartNavigationHistory.length - 1];
      setChartLevel(previousState.level);
      setChartSelectedUserId(previousState.userId);
      setChartSelectedUserName(previousState.userName);
      setChartNavigationHistory((prev) => prev.slice(0, -1));
      setChartCurrentLevelUsers([]);
    } else {
      // Reset to zonal level
      setChartLevel("zonal");
      setChartSelectedUserId(null);
      setChartSelectedUserName("");
      setChartCurrentLevelUsers([]);
    }
  };

  const fetchChartCurrentLevelUsers = async (level, userId) => {
    try {
      let endpoint = "";
      if (level === "branch_manager") {
        endpoint = `/api/users/team-leads-by-branch-manager/${userId}`;
      } else if (level === "team_lead") {
        endpoint = `/api/users/rms-by-team-lead/${userId}`;
      }

      if (endpoint) {
        const { data } = await api.get(endpoint);
        setChartCurrentLevelUsers(data || []);
      }
    } catch (error) {
      console.error("Error fetching chart level users:", error);
      setChartCurrentLevelUsers([]);
    }
  };

  const handleChartUserSelect = (user) => {
    if (chartLevel === "zonal") {
      navigateChartToLevel(
        "branch_manager",
        user.userId || user.id,
        user.name || user.username
      );
    } else if (chartLevel === "branch_manager") {
      navigateChartToLevel(
        "team_lead",
        user.userId || user.id,
        user.name || user.username
      );
    } else if (chartLevel === "team_lead") {
      navigateChartToLevel(
        "relationship_manager",
        user.userId || user.id,
        user.name || user.username
      );
    }
    setShowChartCurrentLevelUsersModal(false);
  };

  // <<< STEP 4: DESTRUCTURE `dashboardData` WITHOUT `kpis` AND `previousMonthKpis` >>>
  const { performanceTrend, todaysStatus, submissionHistory } =
    dashboardData || {
      performanceTrend: [],
      todaysStatus: {},
      submissionHistory: {},
    };

  // The rest of the component remains the same, but we will update the JSX below
  // to use `kpiData`.

  const calendarDays = useMemo(() => {
    const historyData = dashboardData?.submissionHistory;
    const daysArray = [];
    if (!myPerformanceStartDate || !myPerformanceEndDate) return [];
    const start = new Date(
      myPerformanceStartDate.getFullYear(),
      myPerformanceStartDate.getMonth(),
      1
    );
    const finalDate = new Date(myPerformanceEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const blankDays = start.getDay() === 0 ? 6 : start.getDay() - 1;
    for (let i = 0; i < blankDays; i++) {
      daysArray.push({ key: `blank-${i}` });
    }
    let loopDate = new Date(start);
    while (loopDate <= finalDate) {
      const dateStr = formatDateForInput(loopDate);
      const isSunday = loopDate.getDay() === 0;
      const holidayObj = isIndianGovernmentHoliday(loopDate, corporateHolidays);
      const isHoliday = !!holidayObj;
      let status = null;
      let holidayName = null;
      if (loopDate > today) {
        if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        } else {
          status = "future";
        }
      } else if (isHoliday) {
        status = "holiday";
        holidayName = holidayObj.name;
      } else if (isSunday) {
        status = "sunday";
      } else if (historyData && historyData[dateStr]) {
        status = "submitted";
      } else {
        status = "missing";
      }
      daysArray.push({
        key: dateStr,
        day: loopDate.getDate(),
        status,
        dateStr,
        holidayName,
      });
      loopDate.setDate(loopDate.getDate() + 1);
    }
    return daysArray;
  }, [
    dashboardData?.submissionHistory,
    myPerformanceStartDate,
    myPerformanceEndDate,
    corporateHolidays,
  ]);

  const rmPerformanceCalendarDays = useMemo(() => {
    const daysArray = [];
    if (!teamCalendarStartDate || !teamCalendarEndDate) return [];
    let loopDate = new Date(
      teamCalendarStartDate.getFullYear(),
      teamCalendarStartDate.getMonth(),
      1
    );
    const finalDate = new Date(teamCalendarEndDate);
    const todayZero = new Date();
    todayZero.setHours(0, 0, 0, 0);
    const blankDays = loopDate.getDay() === 0 ? 6 : loopDate.getDay() - 1;
    for (let i = 0; i < blankDays; i++) {
      daysArray.push({ key: `perf-${i}` });
    }
    const activeRMs = rms.filter((rm) => !rm.leaveStatus);
    const totalRMs = activeRMs.length;
    // Include submissions from all team RMs, not just currently active ones
    const teamRMUserIds = new Set(
      rms
        .map((rm) => Number(rm.userId))
        .filter((id) => !isNaN(id) && id !== 0)
    );
    const submissionCounts = {};
    if (teamSubmissionHistory && teamSubmissionHistory.length > 0) {
      teamSubmissionHistory.forEach((entry) => {
        const localDateStr = entry.date;
        const entryUserIdAsNumber = Number(entry.userId);
        // Include submissions from any team RM, regardless of current leave status
        if (teamRMUserIds.has(entryUserIdAsNumber)) {
          if (!submissionCounts[localDateStr]) {
            submissionCounts[localDateStr] = new Set();
          }
          submissionCounts[localDateStr].add(entryUserIdAsNumber);
        }
      });
    }
    while (loopDate <= finalDate) {
      const dateStr = formatDateForInput(loopDate);
      const isSunday = loopDate.getDay() === 0;
      const holidayObj = isIndianGovernmentHoliday(loopDate, corporateHolidays);
      const isHoliday = !!holidayObj;
      const activeMembersOnDate = activeRMs.filter(
        (rm) => !isUserOnLeaveOnDate(rm, dateStr)
      );
      const onLeaveCount = totalRMs - activeMembersOnDate.length;
      const totalTeamRMs = rms.length; // All team RMs, including those on leave
      const totalSubmissions = submissionCounts[dateStr]?.size || 0;
      const submissionPercentage =
        totalTeamRMs > 0
          ? Math.round((totalSubmissions / totalTeamRMs) * 100)
          : 0;
      // Check if any team RM submitted on this date
      const hasAnyTeamSubmission = totalSubmissions > 0;

      let status = "future";
      let holidayName = null;
      if (loopDate <= todayZero) {
        if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        } else if (isSunday) {
          status = "sunday";
        } else if (
          onLeaveCount > 0 &&
          onLeaveCount >= activeMembersOnDate.length
        ) {
          status = "on_leave";
        } else if (hasAnyTeamSubmission) {
          status = "submitted";
        } else {
          status = "missing";
        }
      } else if (isHoliday) {
        status = "holiday";
        holidayName = holidayObj.name;
      }

      daysArray.push({
        key: `perf-${dateStr}`,
        day: loopDate.getDate(),
        percentage: submissionPercentage,
        dateStr,
        status,
        holidayName,
      });
      loopDate.setDate(loopDate.getDate() + 1);
    }
    return daysArray;
  }, [
    teamSubmissionHistory,
    rms,
    teamCalendarStartDate,
    teamCalendarEndDate,
    corporateHolidays,
  ]);

  const getTopPerformers = useMemo(() => {
    if (!rms.length) return [];
    return [...rms].sort(
      (a, b) =>
        getMetric(b, selectedLeaderboardMetric) -
        getMetric(a, selectedLeaderboardMetric)
    );
  }, [rms, selectedLeaderboardMetric]);

  if (isLoading && !dashboardData && currentSection === "dashboard") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />{" "}
        <Typography sx={{ ml: 2 }}>Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  return (
    <div>
      {/* ... (The top banner and header remain the same) ... */}
      {isOnLeave && (
        <div
          style={{
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "white",
            padding: "20px 32px",
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            position: "sticky",
            top: 0,
            zIndex: 10001,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            borderBottom: "3px solid #ff6b35",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            minHeight: "60px",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(255, 107, 53, 0.2)",
              borderRadius: "50%",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <FiClock style={{ fontSize: "24px", color: "#ff6b35" }} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Currently on Leave
            </div>
            <div style={{ fontSize: "14px", opacity: "0.9" }}>
              Day {leaveDurationDays} of your leave period{" "}
              {leaveEndDate &&
                ` • Returns: ${new Date(leaveEndDate).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )}`}
            </div>
          </div>
        </div>
      )}
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo-container">
              <div className="logo">
                {" "}
                <img src={logo1} alt="Logo" />{" "}
              </div>
              <span className="logo-text">Aionion Capital</span>
            </div>
            <div className="header-title-group">
              <h1>{toTitleCase(userRole)} Dashboard</h1>
              <span className="performance-tag">Performance Tracking</span>
            </div>
          </div>
          <div className="header-right">
            <nav className="nav-tabs">
              <button
                className={`nav-tab ${
                  currentSection === "dashboard" ? "active" : ""
                }`}
                onClick={() => navigate("/team-lead-dashboard/dashboard")}
              >
                {" "}
                <FiBarChart2 /> Dashboard{" "}
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "input" || currentSection === "input-for"
                    ? "active"
                    : ""
                }`}
                onClick={() => navigate("/team-lead-dashboard/input")}
              >
                {" "}
                <FiPlus /> Input{" "}
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "monthly-report" ? "active" : ""
                }`}
                onClick={() => navigate("/team-lead-dashboard/monthly-report")}
              >
                {" "}
                <FiFileText /> Monthly Report{" "}
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "user-management" ? "active" : ""
                }`}
                onClick={() => navigate("/team-lead-dashboard/user-management")}
              >
                {" "}
                <FiUsers /> User Management{" "}
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "notifications" ? "active" : ""
                }`}
                onClick={() => navigate("/team-lead-dashboard/notifications")}
              >
                {" "}
                <FiBell /> Notifications{" "}
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "settings" ? "active" : ""
                }`}
                onClick={() => navigate("/team-lead-dashboard/settings")}
              >
                {" "}
                <FiSettings /> Settings{" "}
              </button>
            </nav>
            <div className="user-profile">
              <img
                src={user.profile_photo || defaultAvatar}
                alt="Profile"
                className="profile-picture"
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
              <div className="user-info">
                <span className="user-name">{user.name || userName}</span>
                <span className="user-role">{toTitleCase(userRole)}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        {(currentSection === "input" || currentSection === "input-for") && (
          <SalesTracker
            onBackClick={() => navigate("/team-lead-dashboard/dashboard")}
            onEntrySubmit={fetchSectionData}
            userId={targetUserId}
            entryDate={entryDateForTracker}
            isOnLeave={isOnLeave}
          />
        )}
        {currentSection === "dashboard" && (
          <div className="dashboard-grid">
            <section className="card todays-status-card">
              <div className="card-header">
                <h2>My Status Today</h2>
                <div className="live-indicator">
                  {" "}
                  {(() => {
                    const date = new Date();
                    const day = date.getDate();
                    const month = date.toLocaleString("en-GB", {
                      month: "short",
                    });
                    const year = date.getFullYear();
                    return `${day} ${month},${year}`;
                  })()}{" "}
                </div>
              </div>
              <div className="status-boxes">
                {todaysStatus.submitted ? (
                  <div className="status-box submitted">
                    <div className="status-content">
                      <h3>Submitted</h3> <p>Your entry is complete</p>
                      <span>
                        {" "}
                        <FiClock /> Last updated at{" "}
                        {todaysStatus.submissionTime &&
                          new Date(
                            todaysStatus.submissionTime
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}{" "}
                      </span>
                    </div>
                    <div className="status-icon-wrapper green">
                      {" "}
                      <FiCheckCircle />{" "}
                    </div>
                  </div>
                ) : (
                  <div className="status-box not-submitted">
                    <div className="status-content">
                      <h3>Not Submitted</h3> <p>No entry for today yet</p>
                      <span>Click Quick Entry to submit.</span>
                    </div>
                    <div className="status-icon-wrapper grey">
                      {" "}
                      <FiClock />{" "}
                    </div>
                  </div>
                )}
                {/* <<< STEP 5: UPDATE JSX TO USE `kpiData` >>> */}
                <Tooltip
                  title={
                    <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                      {" "}
                      <div>
                        {" "}
                        Avg Daily Calls Made:{" "}
                        {kpiData.kpis.avgDailyCalls || "0.0"}{" "}
                      </div>{" "}
                      <div>
                        Avg Sales Closed: {kpiData.kpis.avgSalesClose || "0.0"}
                      </div>{" "}
                      <div>
                        {" "}
                        Total Appointments Kept:{" "}
                        {kpiData.kpis.appointmentsKept || 0}{" "}
                      </div>{" "}
                    </div>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        fontSize: "14px",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                        minWidth: "220px",
                        lineHeight: "1.6",
                      },
                    },
                    arrow: { sx: { color: "#ffffff" } },
                  }}
                >
                  <div className="status-box quick-stats">
                    <div className="status-content">
                      <h3>Quick Stats</h3> <p>This month's summary</p>
                      <span>
                        {" "}
                        <FiTrendingUp /> Avg daily calls:{" "}
                        {kpiData.kpis.avgDailyCalls || 0}{" "}
                      </span>
                      <span>
                        {" "}
                        <FiAward /> Top activity:{" "}
                        {(() => {
                          const activities = {
                            "Telephone Sales Calls":
                              kpiData.kpis.telephoneSalesCalls || 0,
                            "Face To Face": kpiData.kpis.faceToFace || 0,
                            "Appointments Kept":
                              kpiData.kpis.appointmentsKept || 0,
                            "Sales Close": kpiData.kpis.salesClose || 0,
                          };
                          const topActivity = Object.keys(activities).reduce(
                            (a, b) => (activities[a] > activities[b] ? a : b)
                          );
                          return topActivity || "N/A";
                        })()}{" "}
                      </span>
                    </div>
                    <div className="status-icon-wrapper blue">
                      {" "}
                      <FiTrendingUp />{" "}
                    </div>
                  </div>
                </Tooltip>
              </div>
            </section>

            <section className="card quick-entry-card">
              <div
                className="quick-entry-icon"
                onClick={() => navigate("/team-lead-dashboard/input")}
              >
                {" "}
                <FiPlus />{" "}
              </div>
              <h2>
                {" "}
                {todaysStatus.submitted
                  ? "Edit My Entry"
                  : "My Quick Entry"}{" "}
              </h2>
              <button
                className="submit-entry-btn"
                onClick={() => navigate("/team-lead-dashboard/input")}
              >
                {" "}
                {todaysStatus.submitted
                  ? "Edit Entry"
                  : "Submit Today's Entry"}{" "}
              </button>
              <span className="secure-note">
                {" "}
                <FiLock /> Secure & Encrypted{" "}
              </span>
            </section>

            {/* <<< STEP 5 (CONTINUED): UPDATE ALL KPI CARDS >>> */}
            <div
              className="kpi-cards-container"
              style={{ gridColumn: "1 / -1" }}
            >
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>Aggregated Points</h4>{" "}
                  <FiAward className="stat-icon trophy" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis.aggregatedPoints || 0}
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Last month: {kpiData.previousMonthKpis?.aggregatedPoints || 0}
                </p>{" "}
              </section>
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>Monthly Target</h4>{" "}
                  <FiTarget className="stat-icon target" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis && 1000 > 0
                      ? Math.round((kpiData.kpis.aggregatedPoints / 1000) * 100)
                      : 0}
                    %
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Current: {kpiData.kpis.aggregatedPoints || 0} / 1000
                </p>{" "}
              </section>
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>New Sales Calls</h4>{" "}
                  <FiUsers className="stat-icon interactions" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis.salesInteractions || 0}
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Last month:{" "}
                  {kpiData.previousMonthKpis?.salesInteractions || 0}
                </p>{" "}
              </section>
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>Avg Daily Calls</h4>{" "}
                  <FiPhoneCall className="stat-icon target" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis.avgDailyCalls || "0.0"}
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Last month:{" "}
                  {kpiData.previousMonthKpis?.avgDailyCalls || "0.0"}
                </p>{" "}
              </section>
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>Avg Sales Close</h4>{" "}
                  <FiTrendingUp className="stat-icon conversion" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis.avgSalesClose || "0.0"}
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Last month:{" "}
                  {kpiData.previousMonthKpis?.avgSalesClose || "0.0"}
                </p>{" "}
              </section>
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>Appts. Kept</h4>{" "}
                  <FiCheckSquare className="stat-icon interactions" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis.appointmentsKept || 0}
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Last month: {kpiData.previousMonthKpis?.appointmentsKept || 0}
                </p>{" "}
              </section>
              <section className="card stat-card">
                {" "}
                <div className="stat-header">
                  {" "}
                  <h4>Conversion Rate</h4>{" "}
                  <FiBarChart2 className="stat-icon conversion" />{" "}
                </div>{" "}
                <div className="stat-main">
                  {" "}
                  <span className="stat-value">
                    {kpiData.kpis.conversionRate || 0}%
                  </span>{" "}
                </div>{" "}
                <p className="stat-period">
                  Last month: {kpiData.previousMonthKpis?.conversionRate || 0}%
                </p>{" "}
              </section>
            </div>

            {/* ... The rest of the component from here down is unchanged ... */}
            {/* It will correctly use the dynamic `dashboardData` for charts and tables. */}

            <section
              className="card rm-details-table-card"
              style={{ gridColumn: "1 / -1" }}
            >
              <div className="card-header">
                <h2>Relationship Managers Details</h2>
                <div className="header-controls-container">
                  <div className="date-range-filters">
                    <span className="date-label">From</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(teamDataStartDate)}
                      onChange={(e) =>
                        setTeamDataStartDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <span className="date-label">To</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(teamDataEndDate)}
                      onChange={(e) =>
                        setTeamDataEndDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                  </div>
                  <div className="search-container">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search RMs by name"
                      value={dashboardSearchTerm}
                      onChange={(e) => setDashboardSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <TableContainer
                component={Paper}
                sx={{ maxHeight: 400, overflow: "auto", mt: 2 }}
              >
                <Table stickyHeader sx={{ minWidth: 1600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>RM Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Team Lead</TableCell>
                      <TableCell>Branch Manager</TableCell>
                      <TableCell>Zonal Head</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rms
                      .filter((rm) =>
                        (rm.name || rm.username || "")
                          .toLowerCase()
                          .includes(dashboardSearchTerm.toLowerCase())
                      )
                      .map((rm, index) => (
                        <TableRow
                          key={`rm-details-${rm.userId || rm.id || index}`}
                          sx={
                            rm.leaveStatus
                              ? { backgroundColor: "rgba(255, 199, 44, 0.08)" }
                              : {}
                          }
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              fontWeight: "bold",
                              cursor: "pointer",
                              color: "var(--primary-purple)",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() =>
                              navigate("/team-lead-dashboard/user-management")
                            }
                          >
                            {rm.name || rm.username}
                          </TableCell>
                          <TableCell>
                            {rm.leaveStatus ? (
                              <Tooltip
                                title={`On leave from ${formatNotificationDate(
                                  rm.leaveStartDate
                                )}${
                                  rm.leaveEndDate
                                    ? ` to ${formatNotificationDate(
                                        rm.leaveEndDate
                                      )}`
                                    : ""
                                }`}
                                arrow
                              >
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: "#D97706",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiClock size={14} /> On Leave
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box
                                sx={{ color: "#16A34A", fontWeight: "bold" }}
                              >
                                Active
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{rm.teamLeadName || "N/A"}</TableCell>
                          <TableCell>{rm.branchManagerName || "N/A"}</TableCell>
                          <TableCell>{rm.zonalHeadName || "N/A"}</TableCell>
                          <TableCell>{rm.directorName || "N/A"}</TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "telephoneSalesCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "faceToFace")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "appointmentMade")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "appointmentKept")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "factFind")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "serviceReviews")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "socialCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "highIncomeWealthy")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "referrals")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "closingInterview")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "salesClose")}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </section>

            <div className="performance-row">
              <section className="card submission-history-card">
                <div className="card-header">
                  <h2>Submission History</h2>
                  <div className="date-range-filters">
                    <span className="date-label">From</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(myPerformanceStartDate)}
                      onChange={(e) =>
                        setMyPerformanceStartDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <span className="date-label">To</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(myPerformanceEndDate)}
                      onChange={(e) =>
                        setMyPerformanceEndDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                  </div>
                </div>
                <div className="calendar-grid-wrapper">
                  <div className="calendar-sticky-header">
                    {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                      <div key={idx} className="calendar-day-header">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="calendar-grid">
                    {calendarDays.map((dayInfo) => {
                      if (!dayInfo.day)
                        return (
                          <div
                            key={`submission-blank-${dayInfo.key}`}
                            className="calendar-day"
                          ></div>
                        );
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const entryDateObj = new Date(
                        dayInfo.dateStr.replace(/-/g, "/")
                      );
                      const daysDiff = Math.floor(
                        (today - entryDateObj) / (1000 * 60 * 60 * 24)
                      );
                      const isClickable = entryDateObj <= today;
                      const isEditable = daysDiff >= 0 && daysDiff <= 2;
                      let className = `calendar-day ${dayInfo.status || ""}`;
                      if (isClickable) className += " clickable";
                      return (
                        <Tooltip
                          key={`submission-${dayInfo.key}`}
                          title={
                            dayInfo.status === "holiday" && dayInfo.holidayName
                              ? dayInfo.holidayName
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <div
                            className={className}
                            onClick={() =>
                              isClickable &&
                              navigate(
                                `/team-lead-dashboard/input/${dayInfo.dateStr}`
                              )
                            }
                          >
                            {dayInfo.status === "submitted" && (
                              <FiCheckCircle />
                            )}
                            {dayInfo.status === "holiday" && (
                              <span className="holiday-icon">🏖️</span>
                            )}
                            {dayInfo.status === "missing" && (
                              <span className="missing-icon">&times;</span>
                            )}
                            {dayInfo.status && !isEditable && daysDiff > 2 && (
                              <FiLock className="lock-icon" />
                            )}
                            <span>{dayInfo.day}</span>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item holiday">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                  <div className="legend-item">
                    <FiLock style={{ marginRight: "4px" }} /> View Only
                  </div>
                </div>
              </section>
              <section className="card my-performance-card">
                <div className="card-header chart-controls-header">
                  <div className="filter-row filter-row-top">
                    <h2>Performance Chart</h2>
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="field-filters">
                  <div className="field-filter">
                    <label htmlFor="field-select1">Metric 1:</label>
                    <select
                      id="field-select1"
                      value={selectedField1}
                      onChange={(e) => setSelectedField1(e.target.value)}
                      className="field-select"
                    >
                      {chartFieldOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-filter">
                    <label htmlFor="field-select2">Metric 2:</label>
                    <select
                      id="field-select2"
                      value={selectedField2}
                      onChange={(e) => setSelectedField2(e.target.value)}
                      className="field-select"
                    >
                      {chartFieldOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <span className="legend-dot blue-dot" />
                    {toTitleCase(selectedField1)}
                  </div>
                  {selectedField2 !== "none" && (
                    <div className="legend-item">
                      <span className="legend-dot green-dot" />
                      {toTitleCase(selectedField2)}
                    </div>
                  )}
                </div>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={performanceTrend}
                      margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        strokeOpacity={0.2}
                      />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                      {selectedField2 !== "none" && (
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 10 }}
                        />
                      )}
                      <ChartTooltip
                        wrapperStyle={{ zIndex: 9999 }}
                        contentStyle={{
                          backgroundColor: "var(--card-bg, #ffffff)",
                          border: "1px solid var(--border-color, #e0e0e0)",
                          borderRadius: "8px",
                          boxShadow: "var(--card-shadow)",
                        }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey={selectedField1}
                        stroke="#4A90E2"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name={toTitleCase(selectedField1)}
                      />
                      {selectedField2 !== "none" && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey={selectedField2}
                          stroke="#28a745"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name={toTitleCase(selectedField2)}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </section>
            </div>

            <div className="team-performance-row">
              <section className="card rm-performance-calendar-card">
                <div className="card-header">
                  <h2>Team Progress Calendar</h2>
                  <div className="date-range-filters">
                    <span className="date-label">From</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(teamCalendarStartDate)}
                      onChange={(e) =>
                        setTeamCalendarStartDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                    <span className="date-label">To</span>
                    <input
                      type="date"
                      className="date-input"
                      value={formatDateForInput(teamCalendarEndDate)}
                      onChange={(e) =>
                        setTeamCalendarEndDate(
                          new Date(e.target.value + "T00:00:00")
                        )
                      }
                    />
                  </div>
                </div>
                <div className="calendar-grid-wrapper">
                  <div className="calendar-grid">
                    {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                      <div key={idx} className="calendar-day-header">
                        {day}
                      </div>
                    ))}
                    {rmPerformanceCalendarDays.map((dayInfo) => (
                      <Tooltip
                        key={dayInfo.key}
                        title={
                          dayInfo.status === "holiday" && dayInfo.holidayName
                            ? dayInfo.holidayName
                            : ""
                        }
                        arrow
                        placement="top"
                      >
                        <div
                          className={`calendar-day ${dayInfo.status || ""} ${
                            dayInfo.day ? "clickable" : ""
                          }`}
                          onClick={() =>
                            dayInfo.day &&
                            dayInfo.status &&
                            handleTeamDateClick(dayInfo.dateStr)
                          }
                        >
                          {dayInfo.day ? (
                            <>
                              {" "}
                              {dayInfo.status === "submitted" && (
                                <FiCheckCircle />
                              )}{" "}
                              {dayInfo.status === "holiday" && (
                                <span className="holiday-icon">🏖️</span>
                              )}
                              {dayInfo.status === "sunday" && (
                                <span className="sunday-icon">S</span>
                              )}
                              {calendarDataLoaded &&
                                dayInfo.day &&
                                dayInfo.status !== "future" && (
                                  <span className="points">
                                    {dayInfo.percentage}%
                                  </span>
                                )}{" "}
                              <span>{dayInfo.day}</span>{" "}
                            </>
                          ) : (
                            <>&nbsp;</>
                          )}
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </div>
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item holiday">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                </div>
              </section>

              <section className="card team-performance-card">
                <div className="card-header chart-controls-header">
                  <div className="filter-row filter-row-top">
                    <h2>Team Performance</h2>
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(teamDataStartDate)}
                        onChange={(e) =>
                          setTeamDataStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(teamDataEndDate)}
                        onChange={(e) =>
                          setTeamDataEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <div
                        className="field-filter"
                        style={{ marginLeft: "16px" }}
                      >
                        <label htmlFor="team-field-select">Metric:</label>
                        <select
                          id="team-field-select"
                          value={selectedTeamField1}
                          onChange={(e) =>
                            setSelectedTeamField1(e.target.value)
                          }
                          className="field-select"
                        >
                          {teamChartFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {transformedTeamData && transformedTeamData.length > 0 ? (
                  <>
                    <div className="chart-legend">
                      <span>
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: "#4A90E2" }}
                        />{" "}
                        {teamChartFieldOptions.find(
                          (opt) => opt.value === selectedTeamField1
                        )?.label || "Total Points"}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary, #666)",
                          marginLeft: "16px",
                        }}
                      >
                        Showing {transformedTeamData.length} team members
                      </span>
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                          data={transformedTeamData}
                          margin={{ top: 5, right: 20, left: -10, bottom: 50 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            strokeOpacity={0.2}
                          />
                          <XAxis
                            dataKey="name"
                            interval={0}
                            tick={{
                              fontSize: 12,
                              angle: -35,
                              textAnchor: "end",
                            }}
                          />
                          <YAxis />
                          <ChartTooltip
                            cursor={{ fill: "var(--faint-bg, #f4f7fa)" }}
                            wrapperStyle={{ zIndex: 9999 }}
                            contentStyle={{
                              backgroundColor: "var(--card-bg, #ffffff)",
                              border: "1px solid var(--border-color, #e0e0e0)",
                              borderRadius: "8px",
                              boxShadow: "var(--card-shadow)",
                            }}
                          />
                          <Bar
                            dataKey={selectedTeamField1}
                            name={
                              teamChartFieldOptions.find(
                                (opt) => opt.value === selectedTeamField1
                              )?.label || selectedTeamField1
                            }
                            fill="#4A90E2"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                ) : (
                  <div
                    className="chart-container"
                    style={{
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <Typography color="textSecondary" gutterBottom>
                      No team members found. Add Relationship Managers to your
                      team to see performance data.
                    </Typography>
                  </div>
                )}
              </section>
            </div>

            <section className="card leaderboard-card">
              <div className="card-header">
                <h2>Team Performance Rankings</h2>
                <div className="leaderboard-controls">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={selectedLeaderboardMetric}
                      onChange={(e) =>
                        setSelectedLeaderboardMetric(e.target.value)
                      }
                      displayEmpty
                    >
                      {leaderboardFieldOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <FiAward className="stat-icon" />
              </div>
              <div
                className="leaderboard-list"
                style={
                  getTopPerformers.length > 3
                    ? { maxHeight: "300px", overflowY: "auto" }
                    : {}
                }
              >
                {getTopPerformers.length > 0 ? (
                  getTopPerformers.map((rm, index) => (
                    <div
                      key={`leaderboard-${rm.userId || rm.id || index}`}
                      className="leaderboard-item"
                    >
                      <span className="rank">{index + 1}</span>
                      <span className="name">{rm.name || rm.username}</span>
                      <span className="points">
                        {getMetric(rm, selectedLeaderboardMetric) || 0}{" "}
                        {leaderboardFieldOptions
                          .find(
                            (opt) => opt.value === selectedLeaderboardMetric
                          )
                          ?.label.includes("Points")
                          ? "pts"
                          : "count"}
                      </span>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    <Typography>No team members to rank.</Typography>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {currentSection === "monthly-report" && <MonthlyReport />}
        {currentSection === "user-management" && <UserManagement />}
        {currentSection === "notifications" && (
          // ... The notifications section is unchanged
          <div className="notifications-section">
            <h2>Notifications & Approvals</h2>
            <div className="notifications-tabs">
              <button
                className={`tab-button ${
                  selectedNotificationTab === "pending-approvals"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("pending-approvals")}
              >
                {" "}
                Pending Approvals ({pendingRMLeaveRequests.length}){" "}
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "approved-requests"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("approved-requests")}
              >
                {" "}
                RMs on Leave ({approvedRMLeaveRequests.length}){" "}
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "rm-submissions" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("rm-submissions")}
              >
                {" "}
                RM Submissions{" "}
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "my-requests" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("my-requests")}
              >
                {" "}
                My Leave Requests{" "}
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "all-notifications"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("all-notifications")}
              >
                {" "}
                All Notifications{" "}
              </button>
            </div>
            {selectedNotificationTab === "my-requests" && (
              <div className="pending-requests-list">
                <h3>My Leave Requests</h3>
                {ownLeaveRequests.length > 0 ? (
                  ownLeaveRequests.map((request) => (
                    <div
                      key={request.leaveRequestId}
                      className="leave-request-item"
                    >
                      <p>
                        <strong>Your</strong> leave request from{" "}
                        <strong>
                          {formatNotificationDate(request.startDate)}
                        </strong>
                        {request.endDate ? (
                          <>
                            {" to "}
                            <strong>
                              {formatNotificationDate(request.endDate)}
                            </strong>
                          </>
                        ) : (
                          ""
                        )}
                        .
                      </p>
                      <p>
                        <strong>Reason:</strong> {request.reason}
                      </p>
                      <div className="status-badge">
                        <span className={`status ${request.status}`}>
                          {toTitleCase(request.status)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No personal leave requests found.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "rm-submissions" && (
              <div className="rm-submissions-list">
                <h3>Recent RM Entry Submissions (Last 7 Days)</h3>
                {rmSubmissions.length > 0 ? (
                  rmSubmissions.map((submission, index) => (
                    <div
                      key={`submission-item-${index}`}
                      className="submission-item"
                    >
                      <span className="submission-indicator submitted" />
                      <div className="submission-info">
                        <Typography variant="body1">
                          <strong>{submission.rmName}</strong> submitted entry
                          for{" "}
                          <strong>
                            {formatNotificationDate(submission.date)}
                          </strong>
                          .
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {submission.points} points •{" "}
                          {submission.telephonesalescalls} calls •{" "}
                          {submission.salesclose} sales
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No recent submissions from your team members.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "pending-approvals" && (
              <div className="pending-approvals-list">
                <h3>RM Leave Requests Pending Approval</h3>
                {pendingRMLeaveRequests.length > 0 ? (
                  pendingRMLeaveRequests.map((request) => (
                    <div
                      key={`pending-${request.leaveRequestId}`}
                      className="leave-request-item"
                    >
                      <div className="request-header">
                        <Typography variant="body1">
                          <strong>
                            {request.requestorName || request.rmUsername}
                          </strong>{" "}
                          requested leave from{" "}
                          <strong>
                            {formatNotificationDate(request.startDate)}
                          </strong>
                          {request.endDate && (
                            <>
                              {" to "}
                              <strong>
                                {formatNotificationDate(request.endDate)}
                              </strong>
                            </>
                          )}
                          .
                        </Typography>
                        <div className="request-actions">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              handleApproveLeaveRequest(request.leaveRequestId)
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              handleOpenLeaveRejectDialog(
                                request.leaveRequestId
                              )
                            }
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                      <Typography variant="body2">
                        <strong>Reason:</strong> {request.reason}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Requested on:{" "}
                        {formatNotificationDate(request.requestedAt)}
                      </Typography>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <FiCheckCircle size={40} style={{ color: "#ccc" }} />
                    <Typography color="textSecondary" sx={{ mt: 2 }}>
                      No pending leave requests from team members.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "approved-requests" && (
              <div className="approved-requests-list">
                <h3>Approved RM Leave Requests</h3>
                {approvedRMLeaveRequests.length > 0 ? (
                  approvedRMLeaveRequests.map((request) => (
                    <div
                      key={`approved-${request.leaveRequestId}`}
                      className="leave-request-item"
                    >
                      <div className="request-header">
                        <Typography variant="body1">
                          <strong>
                            {request.rmName || request.requestorName}
                          </strong>{" "}
                          is on leave from{" "}
                          <strong>
                            {formatNotificationDate(request.startDate)}
                          </strong>
                          {request.endDate && (
                            <>
                              {" to "}
                              <strong>
                                {formatNotificationDate(request.endDate)}
                              </strong>
                            </>
                          )}
                          .
                        </Typography>
                        <div className="request-actions">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() =>
                              handleOpenLeaveRevokeDialog(
                                request.leaveRequestId
                              )
                            }
                          >
                            Revoke
                          </Button>
                        </div>
                      </div>
                      <Typography variant="body2">
                        <strong>Reason:</strong> {request.reason}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Approved on:{" "}
                        {formatNotificationDate(request.approvedAt)}
                      </Typography>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No approved leave requests found.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "all-notifications" && (
              <div className="notifications-list">
                <h3>All Notifications</h3>
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div key={notification.id} className="notification-item">
                      <div className="notification-content">
                        <Typography variant="body1">
                          {notification.message}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatNotificationDate(notification.date)}
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No notifications available.
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {currentSection === "settings" && (
          <ProfilePage
            profile={user}
            profilePhotoUrl={user.profile_photo}
            onProfileUpdate={fetchProfileForHeader}
          />
        )}
      </main>

      {/* ... (All dialogs at the bottom remain the same) ... */}
      <Dialog
        open={showTeamSubmissionsModal}
        onClose={() => setShowTeamSubmissionsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Submissions for {selectedTeamDate}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Missing Submissions ({missingRMs.length}):
          </Typography>
          {missingRMs.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  {" "}
                  <TableRow>
                    {" "}
                    <TableCell>Name</TableCell> <TableCell>Username</TableCell>{" "}
                  </TableRow>{" "}
                </TableHead>
                <TableBody>
                  {" "}
                  {missingRMs.map((rm) => (
                    <TableRow key={`missing-${rm.user_id}`}>
                      {" "}
                      <TableCell>{rm.name}</TableCell>{" "}
                      <TableCell>{rm.username}</TableCell>{" "}
                    </TableRow>
                  ))}{" "}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {" "}
              All team members have submitted their entries for this date.{" "}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {" "}
          <Button onClick={() => setShowTeamSubmissionsModal(false)}>
            Close
          </Button>{" "}
        </DialogActions>
      </Dialog>

      <Dialog
        open={leaveRequestDialogOpen}
        onClose={() => setLeaveRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Submit a leave request.
          </Typography>
          <TextField
            label="Reason"
            value={leaveRequestForm.reason}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                reason: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            multiline
            rows={3}
            variant="outlined"
          />
          <TextField
            label="Start Date"
            type="date"
            value={leaveRequestForm.start_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                start_date: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={leaveRequestForm.end_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                end_date: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRequestDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitLeaveRequest}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={leaveRejectDialogOpen}
        onClose={() => setLeaveRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {" "}
            Please provide a reason for rejecting the leave request.{" "}
          </Typography>
          <TextField
            label="Rejection Reason"
            value={leaveRejectionReason}
            onChange={(e) => setLeaveRejectionReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectLeaveRequest}
            variant="contained"
            color="error"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRevokeDialogOpen}
        onClose={() => setLeaveRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke Leave Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {" "}
            Please provide a reason for revoking the approved leave request.
            This action will immediately end the employee's leave status.{" "}
          </Typography>
          <TextField
            label="Revocation Reason"
            value={leaveRevocationReason}
            onChange={(e) => setLeaveRevocationReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
            placeholder="e.g., Urgent project requirements, Business needs changed, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRevokeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeLeaveRequest}
            variant="contained"
            color="error"
          >
            {" "}
            Revoke Approval{" "}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamLeadDashboard;
